import React, { useState, useEffect } from 'react';
import { scanGlassPricingDirectories, loadGlassTypesFromDirectory } from './utils/directoryScanner';
import { GlassType } from './types/glassTypes';

const DirectoryScannerTest: React.FC = () => {
  const [directories, setDirectories] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('whole');
  const [selectedSource, setSelectedSource] = useState<string>('CLG');
  const [glassTypes, setGlassTypes] = useState<GlassType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Function to scan directories
  const handleScanDirectories = async () => {
    try {
      setLoading(true);
      setError(null);
      const dirs = await scanGlassPricingDirectories();
      setDirectories(dirs);
      console.log('Available directories:', dirs);
      
      if (dirs.length > 0 && !dirs.includes(selectedGroup)) {
        setSelectedGroup(dirs[0]);
      }
    } catch (err) {
      console.error('Error scanning directories:', err);
      setError(`Error scanning directories: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to load glass types
  const handleLoadGlassTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const types = await loadGlassTypesFromDirectory(selectedGroup, selectedSource);
      setGlassTypes(types);
      console.log(`Loaded ${types.length} glass types from ${selectedGroup}/${selectedSource}`);
    } catch (err) {
      console.error('Error loading glass types:', err);
      setError(`Error loading glass types: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format thickness for display
  const formatThickness = (thickness: number): string => {
    if (thickness === 0.0625) return '1/16"';
    if (thickness === 0.125) return '1/8"';
    if (thickness === 0.1875) return '3/16"';
    if (thickness === 0.25) return '1/4"';
    if (thickness === 0.375) return '3/8"';
    if (thickness === 0.5) return '1/2"';
    if (thickness === 0.5625) return '9/16"';
    if (thickness === 0.75) return '3/4"';
    if (thickness === 1) return '1"';
    if (thickness === 1.3125) return '1 5/16"';
    return `${thickness}"`;
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-blue-600 mb-6">Directory Scanner Test</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Available Groups</h2>
        <button 
          onClick={handleScanDirectories}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-300"
        >
          {loading ? 'Scanning...' : 'Scan Directories'}
        </button>
        
        {directories.length > 0 && (
          <div className="mt-4">
            <h3 className="font-medium mb-2">Found Directories:</h3>
            <ul className="list-disc pl-5">
              {directories.map(dir => (
                <li key={dir}>{dir}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Load Glass Types by Group</h2>
        
        <div className="flex flex-wrap gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-40"
            >
              {directories.length > 0 ? (
                directories.map(dir => (
                  <option key={dir} value={dir}>{dir}</option>
                ))
              ) : (
                <option value="whole">whole</option>
              )}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-40"
            >
              <option value="CLG">CLG</option>
              <option value="OldCastle">OldCastle</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button 
              onClick={handleLoadGlassTypes}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-300"
            >
              {loading ? 'Loading...' : 'Load Glass Types'}
            </button>
          </div>
        </div>
        
        {glassTypes.length > 0 ? (
          <div>
            <h3 className="font-medium mb-2">Loaded {glassTypes.length} Glass Types:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {glassTypes.slice(0, 9).map(type => (
                <div key={type.id} className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-all bg-gray-50">
                  <h4 className="text-lg font-medium">{type.name}</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Type:</span> {type.type}</p>
                    <p><span className="font-medium">Thickness:</span> {type.thicknessStr || formatThickness(type.thickness)}</p>
                    <p><span className="font-medium">Weight:</span> {type.lbsPerSqFt} lbs/sq ft</p>
                    <p><span className="font-medium">Price:</span> {formatCurrency(type.price)}</p>
                  </div>
                </div>
              ))}
            </div>
            {glassTypes.length > 9 && (
              <p className="text-gray-500 mt-4">Showing 9 of {glassTypes.length} glass types...</p>
            )}
          </div>
        ) : (
          <p className="text-gray-500">No glass types loaded yet. Click "Load Glass Types" to fetch data.</p>
        )}
      </div>
    </div>
  );
};

export default DirectoryScannerTest;
